// @flow
// $FlowFixMe
import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'
import Modal from './Modal'
import LoadingCircleSmall from 'src/components/Icons/LoadingCircleSmall'
import { theme, StyledP } from 'src/theme/globalStyle'
import { media } from 'src/theme/style-utils'
import Tick from 'src/components/Icons/Tick'
import Warning from 'src/assets/icons/warning.svg'

type Props = {
  open: boolean,
  step?: 1 | 2 | 3
}
const PaymentModal = ({ open, step = 1 }: Props) => {
  const steps = ['Verifying details', 'Processing payment', 'Finalizing booking']

  useEffect(() => {
    const unloadHandler = event => {
      event.returnValue = `You might lose your booking if you close this window.`
      return 'You might lose your booking if you close this window.'
    }

    window.addEventListener('beforeunload', unloadHandler)

    return () => {
      window.removeEventListener('beforeunload', unloadHandler)
    }
  }, [])
  return (
    <Modal open={open} toClose={() => {}} step={step}>
      <PaymentModal.container>
        <PaymentModal.wrapper>
          <PaymentModal.title>Please wait...</PaymentModal.title>
          <StepsWrapper>
            <ProgressDots />
            <ProgressDoneWrapper step={step} className="ProgressDoneWrapper">
              <ProgressDone step={step} />
            </ProgressDoneWrapper>
            <StepContainer>
              {steps.map((curStep, index) => (
                <Step key={`${index}_${curStep}`}>
                  {step > index + 1 ? <DoneStep /> : <PendingStep index={index} step={step} />}
                  <StepText active={step > index + 1}>{curStep}</StepText>
                </Step>
              ))}
            </StepContainer>
          </StepsWrapper>
        </PaymentModal.wrapper>
        <PaymentModal.wrapper lowerLayout>
          <PaymentModal.waringText>
            <img src={Warning} height="29px" width="29px" alt="warning" />{' '}
            {`Please be patient while we process your booking. Don't refresh or close this page`}
          </PaymentModal.waringText>
        </PaymentModal.wrapper>
      </PaymentModal.container>
    </Modal>
  )
}

PaymentModal.container = styled.section`
  display: flex;
  flex-direction: column;
  max-width: 90%;
`

PaymentModal.wrapper = styled.section`
  background: white;
  box-sizing:border-box;
  margin: auto;
  width: 100%;
  ${props =>
    props.lowerLayout
      ? css`
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 6px;
          border-bottom-left-radius: 6px;
        `
      : css`
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
          border: none;
          border-bottom: 1px solid rgba(0, 0, 0, 0.6);
        `}
  background-color: ${theme.white};
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  color: black;
  text-align: left;
  ${media.tabletLarge`
      padding: 20px 40px;
      width: 100%;
      padding: 40px;
      max-width: 540px;
      max-height: 540px;
  `}
`
PaymentModal.title = styled.h1`
  font-weight: 500;
  margin: 0 0 36px 0;
`
PaymentModal.divider = styled.hr`
  display: block;
  margin: 0;
  width: 100%;
  border-style: inset;
  border-width: 0.6px;
  border-color: rgba(0, 0, 0, 0.6);
`

PaymentModal.waringText = styled.p`
  font-size: 16px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: 0.32px;
  color: ${theme.black};
  display: flex;
  & > img {
    margin-right: 20px;
  }
`

PaymentModal.stepInfo = styled(StyledP)`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.32px;
  margin: 0;
  color: ${props => (props.active ? theme.black : theme.lightText)};
`

const StepsWrapper = styled.div`
  width: 100%;
  position: relative;
  min-height: 200px;
`
const ProgressDots = styled.div`
  margin-left: 15px;
  height: 200px;
  top: 0;
  bottom: 0;
  background-image: url('${process.env.PUBLIC_URL}/single-dot.svg');
  background-position: left;
  background-size: 3px 11px;
  background-repeat: repeat-y;
  box-sizing: border-box;
  width: 3px;
`
const StepContainer = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  background: transparent;
  text-transform: capitalize;
`

const Step = styled.div`
  display: flex;
  justify-content: flex-start;
  font-size: 16px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: 0.32px;
  color: #9dabba;
  background: white;
  height: 29px;
`
const StepNumber = styled.div`
  height: 29px;
  width: 29px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  ${props =>
    props.invert
      ? css`
          border: none;
          background-color: #aedd99;
          margin-left: 2px;
        `
      : css`
          border: 2px solid #e1e4e9;
        `}
`
const LoadingStep = styled(StepNumber)`
  position: relative;
  border: none;
`
const LoadingStepNumber = styled.span`
  position: absolute;
  top: 10%;
  left: 37%;
`
const StepText = styled.span`
  margin-left: 20px;
  margin-top: 2px;
  color: ${props => (props.active ? theme.black : theme.lightText)};
`

const ProgressDone = styled.div`
  background-color: #aedd99;
  height: 1px;
  width: 2px;
  transform: ${props => {
    let returnVal = `none`
    switch (props.step) {
      case 2:
        returnVal = `scaleY(55)`
        break
      case 3:
        returnVal = `scaleY(141)`
        break
      default:
        returnVal = `none`
        break
    }
    return returnVal
  }};
  transition: transform 0.25s linear;
  transform-origin: top;
`

const ProgressDoneWrapper = styled.div`
  position:absolute;
  background: white;
  top: 29px;
  left: 14px;
  width: 4px;
  opacity:${props => (props.step > 1 ? 1 : 0)}
  height: ${props => {
    let returnVal = `none`
    switch (props.step) {
      case 2:
        returnVal = `55px`
        break
      case 3:
        returnVal = `141px`
        break
      default:
        returnVal = 0
        break
    }
    return returnVal
  }};
  
  display:flex;
  flex-direction:column;
  justify-content:stretch;
  align-items:center;
`

export default PaymentModal
type StepProps = {
  index: number
}
const ProcessingStep = ({ index }: StepProps) => {
  return (
    <React.Fragment>
      <LoadingStep>
        <LoadingCircleSmall />
        <LoadingStepNumber>{index + 1}</LoadingStepNumber>
      </LoadingStep>
    </React.Fragment>
  )
}

type PendingStepProps = {
  index: number,
  step: number
}
const PendingStep = ({ index, step }: PendingStepProps) =>
  step === index + 1 ? <ProcessingStep index={index} /> : <StepNumber>{index + 1}</StepNumber>

const DoneStep = () => (
  <StepNumber invert>
    <Tick color={theme.white} />
  </StepNumber>
)



// WEBPACK FOOTER //
// ./src/containers/SecureRoom/components/MobileHeader/PaymentModal.js